import { Component, Input, Output, EventEmitter, forwardRef, OnChanges, SimpleChanges, OnInit } from '@angular/core'
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormControl } from '@angular/forms'
import { Observable, of, BehaviorSubject, from } from 'rxjs'
import { startWith, map, switchMap, catchError, debounceTime, distinctUntilChanged, take, tap } from 'rxjs/operators'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import { MatFormFieldAppearance } from '@angular/material/form-field'

@Component({
  selector: 'inscricoes-autocomplete-key-value',
  templateUrl: './autocomplete-key-value.component.html',
  styleUrls: ['./autocomplete-key-value.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteKeyValueComponent),
      multi: true,
    },
  ],
})
export class AutocompleteKeyValueComponent implements ControlValueAccessor, OnChanges, OnInit {
  @Input() options: any[] = []
  @Input() keyProperty: string = 'key'
  @Input() valueProperty: string = 'value'
  @Input() placeholder: string = ''
  @Input() label: string = ''
  @Input() hint: string = ''
  @Input() required: boolean = false
  @Input() debounceTime: number = 300

  @Input() appearance: MatFormFieldAppearance = 'fill'

  @Output() selected = new EventEmitter<any>()

  control = new FormControl()
  filteredOptions: Observable<any[]> = from([])

  protected _optionsArray: any[] = []
  protected _onChange: (value: any) => void = () => {}
  protected _onTouched: () => void = () => {}
  private _initialized = false

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['options']) {
      this.loadOptions()
    }
  }

  ngOnInit() {
    this.setupAutocomplete()
    this._initialized = true

    this.control.valueChanges.subscribe(valor => console.log(valor))
  }

  private loadOptions() {
    if (this.options instanceof Observable) {
      // Carrega os dados UMA única vez se for Observable
      this.options.pipe(take(1)).subscribe(data => {
        this._optionsArray = [...data]
        this.setupAutocomplete()
      })
    } else {
      // Se já for array, usa diretamente
      this._optionsArray = [...this.options]
      this.setupAutocomplete()
    }
  }

  private setupAutocomplete() {
    this.filteredOptions = this.control.valueChanges.pipe(
      debounceTime(300),
      startWith(''),
      map(value => this._filter(value))
    )
  }

  writeValue(value: any): void {
    if (value) {
      const displayValue = typeof value === 'object' ? value[this.valueProperty] : value
      this.control.setValue(displayValue, { emitEvent: false })
    } else {
      this.control.setValue('', { emitEvent: false })
    }
  }

  registerOnChange(fn: any): void {
    this._onChange = fn
  }

  registerOnTouched(fn: any): void {
    this._onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.control.disable() : this.control.enable()
  }

  private _filter(value: string): any[] {
    if (typeof value !== 'string') return this._optionsArray

    const filterValue = this.normalizeText(value.toLowerCase().trim())
    if (!filterValue) return this._optionsArray

    const searchTerms = filterValue.split(/\s+/)
    
    return this._optionsArray.filter(option => {
      if (!option[this.valueProperty]) return false
      
      const optionText = this.normalizeText(option[this.valueProperty].toString().toLowerCase())
      return searchTerms.every(term => optionText.includes(term))
    })
  }


  private normalizeText(text: string): string {
    return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
  }
  onOptionSelected(event: MatAutocompleteSelectedEvent): void {
    const selectedValue = event.option.value
    const completeOption = this._optionsArray.find(option => option[this.valueProperty] === selectedValue)

    if (completeOption) {
      this._onChange(completeOption)
      this.selected.emit(completeOption)
    }
  }

  displayFn(value: any): string {
    return value && value[this.valueProperty] ? value[this.valueProperty] : ''
  }

  clear(): void {
    this.control.setValue(null, { emitEvent: true })
    this._onChange(null)
    this.selected.emit(null)
    this._onTouched()
  }
}
